import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CATEGORY_LABEL, HOSPITAL_STATUS_LABEL, PLAN_LABEL, SMS_TYPE_LABEL, label } from "@/lib/labels";
import { useApp, API } from "@/contexts/AppContext";
import {
  Building2, ArrowLeft, Users, Calendar, MessageSquare, Megaphone,
  UserCog, Phone, MapPin, Heart, Baby, UserRound, HeartPulse, BedDouble, Cross,
} from "lucide-react";

export default function SuperHospitalDetail() {
  const { id } = useParams();
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    axios.get(`${API}/hospitals/${id}/details`).then((r) => setData(r.data)).catch(() => {});
  }, [id]);

  if (!data) return <div className="p-12 text-center text-[#5C6B61]" data-testid="loading-detail">Chargement...</div>;
  const h = data.hospital;
  const c = data.counts;
  const fmt = (d) => d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short", timeZone: "Africa/Kinshasa" }) : "—";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { timeZone: "Africa/Kinshasa" }) : "—";

  const tabs = [
    { key: "overview", label: "Vue d'ensemble", icon: Building2 },
    { key: "staff", label: `Équipe (${data.staff.length})`, icon: UserCog },
    { key: "patients", label: `Patients (${c.patients})`, icon: Users },
    { key: "hospitalized", label: `Hospitalisés (${c.patients_hospitalized || 0})`, icon: BedDouble },
    { key: "deaths", label: `Décès (${c.patients_deceased || 0})`, icon: Cross },
    { key: "appointments", label: `RDV (${c.appointments})`, icon: Calendar },
    { key: "sms", label: `SMS (${c.sms})`, icon: MessageSquare },
    { key: "campaigns", label: `Campagnes (${c.campaigns})`, icon: Megaphone },
  ];

  const cats = [
    { l: "Femmes enceintes", v: c.patients_pregnant, i: Heart, c: "#D96C4A" },
    { l: "Enfants 0-3 ans", v: c.patients_child, i: Baby, c: "#2E7D32" },
    { l: "Troisième âge", v: c.patients_elderly, i: UserRound, c: "#1E4631" },
    { l: "Patients généraux", v: c.patients_general, i: HeartPulse, c: "#5C6B61" },
  ];

  return (
    <div className="space-y-6" data-testid="hospital-detail">
      <Link to="/super-admin/hospitals" className="inline-flex items-center gap-2 text-sm text-[#5C6B61] hover:text-[#1E4631]" data-testid="back-hospitals">
        <ArrowLeft className="w-4 h-4"/> Retour
      </Link>

      <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#1E4631]/10 grid place-items-center">
            <Building2 className="w-7 h-7 text-[#1E4631]"/>
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-[Outfit] font-bold">{h.name}</h1>
            <div className="mt-2 text-sm text-[#5C6B61] flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{h.city}</span>
              <span>·</span>
              <span>{h.contact_email}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5"/>{h.contact_phone}</span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs">
              <span className="px-2 py-0.5 rounded-full font-medium" style={{
                background: h.status === "active" ? "#2E7D3215" : h.status === "pending" ? "#E3A72F15" : "#C6282815",
                color: h.status === "active" ? "#2E7D32" : h.status === "pending" ? "#E3A72F" : "#C62828",
              }}>{label(HOSPITAL_STATUS_LABEL, h.status)}</span>
              <span className="px-2 py-0.5 rounded-full bg-[#FDFBF7] text-[#5C6B61] uppercase">{label(PLAN_LABEL, h.plan)}</span>
              <span className="text-[#5C6B61] ml-2">ID: <code className="text-xs">{h.id}</code></span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap border-b border-[#E5EBE7]">
        {tabs.map((tt) => (
          <button key={tt.key} onClick={() => setTab(tt.key)} data-testid={`tab-${tt.key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition flex items-center gap-2 ${
              tab === tt.key ? "border-[#1E4631] text-[#1E4631]" : "border-transparent text-[#5C6B61] hover:text-[#1E4631]"
            }`}>
            <tt.icon className="w-4 h-4"/> {tt.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-4 gap-4">
            {cats.map((x, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#E5EBE7] p-5">
                <x.i className="w-5 h-5" style={{ color: x.c }}/>
                <div className="mt-3 text-3xl font-[Outfit] font-bold">{x.v}</div>
                <div className="text-xs text-[#5C6B61] mt-1">{x.l}</div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-[#E5EBE7] p-5">
              <div className="text-sm font-semibold mb-1">Activité totale</div>
              <ul className="mt-3 space-y-2 text-sm text-[#5C6B61]">
                <li>Patients : <strong className="text-[#1A1D1A]">{c.patients}</strong></li>
                <li>Hospitalisations en cours : <strong className="text-[#D96C4A]">{c.patients_hospitalized || 0}</strong></li>
                <li>Décès enregistrés : <strong className="text-[#C62828]">{c.patients_deceased || 0}</strong></li>
                <li>Rendez-vous : <strong className="text-[#1A1D1A]">{c.appointments}</strong></li>
                <li>SMS envoyés (MOCK) : <strong className="text-[#1A1D1A]">{c.sms}</strong></li>
                <li>Campagnes diffusées : <strong className="text-[#1A1D1A]">{c.campaigns}</strong></li>
                <li>Membres équipe : <strong className="text-[#1A1D1A]">{data.staff.length}</strong></li>
              </ul>
            </div>
            <div className="bg-white rounded-xl border border-[#E5EBE7] p-5">
              <div className="text-sm font-semibold mb-1">Composition équipe</div>
              <ul className="mt-3 space-y-2 text-sm text-[#5C6B61]">
                {["hospital_admin", "doctor", "nurse", "caregiver", "secretary"].map((r) => {
                  const n = data.staff.filter((s) => s.role === r).length;
                  return <li key={r}>{t.roles[r]} : <strong className="text-[#1A1D1A]">{n}</strong></li>;
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {tab === "staff" && (
        <div className="bg-white rounded-xl border border-[#E5EBE7] divide-y divide-[#E5EBE7]">
          {data.staff.length === 0 ? <div className="p-12 text-center text-[#5C6B61]">Aucun membre</div> :
           data.staff.map((s) => (
            <div key={s.id} className="p-4 flex items-center justify-between" data-testid={`detail-staff-${s.id}`}>
              <div>
                <div className="font-semibold">{s.name}</div>
                <div className="text-xs text-[#5C6B61]">{s.email}</div>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-[#1E4631]/10 text-[#1E4631] uppercase font-bold tracking-wide">{t.roles[s.role] || s.role}</span>{s.specialty && <span className="text-xs text-[#5C6B61] ml-2">{s.specialty}</span>}
            </div>
          ))}
        </div>
      )}

      {tab === "patients" && (
        <div className="bg-white rounded-xl border border-[#E5EBE7] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left text-xs uppercase text-[#5C6B61]">
              <tr><th className="px-5 py-3">Nom</th><th className="px-5 py-3">Téléphone</th><th className="px-5 py-3">Catégorie</th><th className="px-5 py-3">Langue</th><th className="px-5 py-3">Inscrit</th></tr>
            </thead>
            <tbody>
              {data.patients.length === 0 ? <tr><td colSpan="5" className="text-center py-12 text-[#5C6B61]">Aucun patient</td></tr> :
               data.patients.map((p) => (
                <tr key={p.id} className="border-t border-[#E5EBE7]" data-testid={`detail-patient-${p.id}`}>
                  <td className="px-5 py-3 font-medium">{p.name}</td>
                  <td className="px-5 py-3">{p.phone}</td>
                  <td className="px-5 py-3">{label(CATEGORY_LABEL, p.category)}</td>
                  <td className="px-5 py-3 uppercase text-xs">{p.language}</td>
                  <td className="px-5 py-3 text-xs text-[#5C6B61]">{fmt(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "hospitalized" && (
        <div className="bg-white rounded-xl border border-[#E5EBE7] overflow-hidden" data-testid="hospitalized-tab">
          <div className="p-4 bg-[#D96C4A]/5 border-b border-[#D96C4A]/20 text-sm text-[#5C6B61] flex items-start gap-2">
            <BedDouble className="w-4 h-4 text-[#D96C4A] mt-0.5 shrink-0"/>
            <div>
              <strong className="text-[#D96C4A]">{c.patients_hospitalized || 0}</strong> patient{(c.patients_hospitalized||0)>1?"s":""} actuellement hospitalisé{(c.patients_hospitalized||0)>1?"s":""} à {h.name}.
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left text-xs uppercase text-[#5C6B61]">
              <tr>
                <th className="px-5 py-3">Patient</th>
                <th className="px-5 py-3">Catégorie</th>
                <th className="px-5 py-3">Chambre</th>
                <th className="px-5 py-3">Admis le</th>
                <th className="px-5 py-3">Motif</th>
              </tr>
            </thead>
            <tbody>
              {(data.hospitalized_list || []).length === 0 ? (
                <tr><td colSpan="5" className="text-center py-12 text-[#5C6B61]">Aucun patient hospitalisé actuellement</td></tr>
              ) : data.hospitalized_list.map((p) => (
                <tr key={p.id} className="border-t border-[#E5EBE7] hover:bg-[#FDFBF7]/50" data-testid={`hospitalized-${p.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-[#5C6B61]">{p.phone}</div>
                  </td>
                  <td className="px-5 py-3 text-xs">{label(CATEGORY_LABEL, p.category)}</td>
                  <td className="px-5 py-3 font-mono text-xs">{p.room_number || "—"}</td>
                  <td className="px-5 py-3 text-xs">{fmt(p.admission_date)}</td>
                  <td className="px-5 py-3 text-xs text-[#5C6B61]">{p.admission_reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "deaths" && (
        <div className="bg-white rounded-xl border border-[#E5EBE7] overflow-hidden" data-testid="deaths-tab">
          <div className="p-4 bg-[#C62828]/5 border-b border-[#C62828]/20 text-sm text-[#5C6B61] flex items-start gap-2">
            <Cross className="w-4 h-4 text-[#C62828] mt-0.5 shrink-0"/>
            <div>
              <strong className="text-[#C62828]">{c.patients_deceased || 0}</strong> décès enregistré{(c.patients_deceased||0)>1?"s":""} à {h.name}. Information confidentielle — usage administratif uniquement.
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left text-xs uppercase text-[#5C6B61]">
              <tr>
                <th className="px-5 py-3">Patient</th>
                <th className="px-5 py-3">Âge / Catégorie</th>
                <th className="px-5 py-3">Date décès</th>
                <th className="px-5 py-3">Cause</th>
                <th className="px-5 py-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {(data.deceased_list || []).length === 0 ? (
                <tr><td colSpan="5" className="text-center py-12 text-[#5C6B61]">Aucun décès enregistré à ce jour</td></tr>
              ) : data.deceased_list.map((p) => (
                <tr key={p.id} className="border-t border-[#E5EBE7] hover:bg-[#FDFBF7]/50" data-testid={`deceased-${p.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-[#5C6B61]">{p.phone}</div>
                  </td>
                  <td className="px-5 py-3 text-xs">
                    {p.age != null ? `${p.age} ans` : "—"} · {label(CATEGORY_LABEL, p.category)}
                  </td>
                  <td className="px-5 py-3 text-xs font-medium">{fmt(p.deceased_at)}</td>
                  <td className="px-5 py-3 text-xs text-[#C62828]">{p.death_cause || "Non précisé"}</td>
                  <td className="px-5 py-3 text-xs text-[#5C6B61] max-w-xs truncate" title={p.death_notes || ""}>{p.death_notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "appointments" && (
        <div className="bg-white rounded-xl border border-[#E5EBE7] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left text-xs uppercase text-[#5C6B61]">
              <tr><th className="px-5 py-3">Patient</th><th className="px-5 py-3">Date</th><th className="px-5 py-3">Motif</th><th className="px-5 py-3">Médecin</th><th className="px-5 py-3">Rappels</th></tr>
            </thead>
            <tbody>
              {data.appointments.length === 0 ? <tr><td colSpan="5" className="text-center py-12 text-[#5C6B61]">Aucun RDV</td></tr> :
               data.appointments.map((a) => (
                <tr key={a.id} className="border-t border-[#E5EBE7]">
                  <td className="px-5 py-3 font-medium">{a.patient_name}</td>
                  <td className="px-5 py-3">{fmt(a.date)}</td>
                  <td className="px-5 py-3">{a.reason}</td>
                  <td className="px-5 py-3">{a.doctor || "—"}</td>
                  <td className="px-5 py-3">{a.reminders_sent.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "sms" && (
        <div className="bg-white rounded-xl border border-[#E5EBE7] divide-y divide-[#E5EBE7]">
          {data.sms_recent.length === 0 ? <div className="p-12 text-center text-[#5C6B61]">Aucun SMS</div> :
           data.sms_recent.map((s) => (
            <div key={s.id} className="p-4 text-sm">
              <div className="flex items-center gap-3 text-xs text-[#5C6B61]">
                <span className="font-medium text-[#1A1D1A]">{s.phone}</span>
                <span className="px-2 py-0.5 rounded-full bg-[#1E4631]/10 text-[#1E4631]">{label(SMS_TYPE_LABEL, s.type)}</span>
                <span>{fmt(s.sent_at)}</span>
              </div>
              <div className="mt-1.5 text-[#1A1D1A]">{s.message}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "campaigns" && (
        <div className="grid md:grid-cols-2 gap-4">
          {data.campaigns.length === 0 ? <div className="md:col-span-2 bg-white p-12 text-center text-[#5C6B61] rounded-xl border border-[#E5EBE7]">Aucune campagne</div> :
           data.campaigns.map((cp) => (
            <div key={cp.id} className="bg-white rounded-xl border border-[#E5EBE7] p-5">
              <div className="font-[Outfit] font-semibold">{cp.title}</div>
              <div className="mt-1 text-sm text-[#5C6B61]">{cp.message}</div>
              <div className="mt-3 text-xs text-[#5C6B61]">{cp.sent_count} envoyés · {fmt(cp.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
